"use client"

import { useEffect, useState, useTransition } from "react"
import { useLocale, useTranslations } from "next-intl"
import { ProfileControl } from "@owlcat/identity-react"
import { LanguageSwitcher } from "@owlcat/app-shell"
// Shop's theme is next-themes (attribute="class"), NOT @owlcat/theme's cookie
// model — so the toggle MUST drive next-themes, else setTheme is a no-op here.
import { useTheme } from "next-themes"
import { ThemeToggle } from "../theme-toggle"
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@owlcat/ui"

import { localeNames, locales, type Locale } from "@/types/i18n"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { setUserLocale } from "@modules/layout/components/language-selector/actions"

const stripSlash = (u: string) => u.replace(/\/+$/, "")

/** Parent (apex) domain — used to synthesize the accounts + apex fallbacks. */
const PARENT_DOMAIN =
  process.env.NEXT_PUBLIC_OWLCAT_PARENT_DOMAIN || "owlcat.fartusov.com"

/** Central accounts SSO authority (parent-domain owlcat-id cookie). */
const ACCOUNTS_URL = stripSlash(
  process.env.NEXT_PUBLIC_ACCOUNTS_URL || `https://accounts.${PARENT_DOMAIN}`
)

/**
 * HeaderControls — the shop's fleet identity control.
 *
 * We render @owlcat/identity-react <ProfileControl> DIRECTLY (rather than
 * @owlcat/app-shell <AppHeaderControls>) because the shop needs to APPEND a
 * shop-local menu item — "My orders & addresses" → the Medusa storefront
 * account area — which AppHeaderControls does not expose a pass-through for.
 * The dropdown is: [common Profile (accounts)] · [My orders & addresses
 * (shop/Medusa)] · Language ▸ · Theme · Sign out. Language + Theme are rebuilt
 * here from the same @owlcat primitives so the UX matches the fleet dropdown.
 *
 * CLIENT-ONLY: ProfileControl does a credentialed cross-subdomain GET /me (the
 * parent-domain owlcat-id cookie rides along). `returnTo` is the live shop URL
 * so accounts bounces the user back after login/logout.
 *
 * NOTE: the shop's `countryCode` URL segment is the REGION (shipping) — a
 * distinct concept from the UI locale — so the region <CountrySelector> stays
 * in the nav and the language submenu here drives only next-intl's locale via
 * setUserLocale (matching the old LanguageSelector behavior).
 */
export default function HeaderControls() {
  const t = useTranslations("common")
  const locale = useLocale()
  const { resolvedTheme, setTheme } = useTheme()
  const [, startTransition] = useTransition()
  const [mounted, setMounted] = useState(false)
  const [returnTo, setReturnTo] = useState<string | undefined>(undefined)

  // `window` is unavailable during SSR; defer the control (and its live
  // returnTo) to the client so the login href can't mismatch on hydration.
  useEffect(() => {
    setReturnTo(window.location.href)
    setMounted(true)
  }, [])

  if (!returnTo) return null

  const isDark = resolvedTheme === "dark"

  const switchLocale = (next: Locale) => {
    if (next === locale) return
    startTransition(async () => {
      await setUserLocale(next)
      window.location.reload()
    })
  }

  // Shop language config drives next-intl (cookie), NOT the URL segment.
  const language = {
    locale,
    locales,
    localeNames,
    parentDomain: PARENT_DOMAIN,
    label: t("language.selectLanguage"),
    onSelect: (next: string) => switchLocale(next as Locale),
  }

  // AUTHED dropdown extras: store account link + language submenu + theme.
  const menuExtras = (
    <>
      <DropdownMenuItem asChild>
        <LocalizedClientLink href="/account">
          {t("nav.ordersAddresses")}
        </LocalizedClientLink>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          {t("language.selectLanguage")}: {localeNames[locale as Locale] ?? locale}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          {locales.map((l) => (
            <DropdownMenuItem key={l} onSelect={() => switchLocale(l)}>
              {localeNames[l]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      <DropdownMenuItem
        onSelect={(e) => {
          e.preventDefault()
          setTheme(isDark ? "light" : "dark")
        }}
      >
        {t("theme.toggle")}
        {mounted ? (
          <span className="ml-auto text-xs text-muted-foreground">
            {isDark ? "◐" : "☀"}
          </span>
        ) : null}
      </DropdownMenuItem>
    </>
  )

  // ANON has no dropdown → compact language + theme beside the Sign-in button.
  const anonExtras = (
    <>
      <LanguageSwitcher {...language} />
      <ThemeToggle />
    </>
  )

  return (
    <ProfileControl
      locale={locale}
      accountsUrl={ACCOUNTS_URL}
      appId="shop"
      selfPath={process.env.NEXT_PUBLIC_ACCOUNTS_SELF_PATH || "/me"}
      profileUrl={`${ACCOUNTS_URL}/profile?lang=${encodeURIComponent(locale)}`}
      returnTo={returnTo}
      labels={{
        signIn: t("nav.login"),
        signOut: t("nav.logout"),
        account: t("nav.account"),
        profile: t("nav.myAccount"),
        yourApps: t("nav.yourApps"),
      }}
      headerExtras={undefined}
      menuExtras={menuExtras}
      anonExtras={anonExtras}
    />
  )
}
